// Server-side ordnance: grenades, rockets, harpoons, firework shells and their seekers and
// canopies, the Big One's beacon, deployed turrets, and the ordnance crates special weapons
// come from. The Game owns one Arsenal and calls a handful of hooks; everything it spawns is
// announced once as an event and flown by every client with the same analytic motion
// (ordPosAt), the way enemy bullets already are.

import { DT, Rng, TICK_RATE, aimDir, clamp, hash32, len2, segPointDist2, secs } from './core';
import { ElemHost, applyProc, tryProc } from './elements';
import { AP, ClassId, ENEMIES, EType, PLAYER_HEIGHT, PLAYER_RADIUS, STAGGER_GRENADE, STAGGER_ORDNANCE, SPLASH_AP_DROP } from './defs';
import { Ev } from './codec';
import type { Enemy } from './enemies';
import { InputFrame, Phase, PlayerState, activeWeaponId, isTargetable, weaponOf } from './player';
import { modsOf } from './player';
import { Canopy, DUEL_BOLT_SPEED, DUEL_BOLT_TURN, Elem, G, GRENADES, GRENADE_BANK, PLASMA_FULL_MUL, GRENADE_GRAVITY, GRENADE_MAX, NO_WEAPON, Payload, Proj, THROW_SPEED, W, WEAPONS, WSlot, WeaponDef, rollCrate, weaponDef } from './weapons';
import type { RayHit, World } from './world';
import { blastWorld } from './destruct';

/** Flown ordnance kinds (on the wire as Ev.OrdSpawn kind). */
export const enum OKind { Grenade = 0, Rocket = 1, Harpoon = 2, Shell = 3, Launch = 4, Beacon = 5, Seeker = 6, BadIdea = 7, Bolt = 8 }
export interface OrdDef { gravity: number; radius: number; life: number; homing: number }
export const ORD: OrdDef[] = [
  { gravity: GRENADE_GRAVITY, radius: 0.18, life: 3.5, homing: 0 },
  { gravity: 0, radius: 0.25, life: 3.2, homing: 0 },
  { gravity: 5, radius: 0.15, life: 2.5, homing: 0 },
  { gravity: GRENADE_GRAVITY, radius: 0.22, life: 3.5, homing: 0 },
  { gravity: 0, radius: 0.2, life: 1.0, homing: 0 },
  { gravity: GRENADE_GRAVITY, radius: 0.2, life: 3.5, homing: 0 },
  { gravity: 0, radius: 0.18, life: 3.0, homing: 7 },
  { gravity: 0, radius: 0.35, life: 9.0, homing: 2.2 },
  /** the Duel Blaster's plasma bolt: slow, curving onto the robot nearest the crosshair (weapons.ts DUEL_BOLT_*) */
  { gravity: 0, radius: 0.22, life: 2.4, homing: DUEL_BOLT_TURN },
];
export const SEEKER_SPEED = 24;
export const LAUNCH_SPEED = 15;
export const BEACON_SECS = 3;
export const CANOPY_SECS = 15;
export const TURRET_SECS = 20;
/** Canopies go out as Ev.Hazard with element CANOPY_EL + Canopy, so clients can tell them from
 *  robot hazards (gas = 7) and draw the dome. */
export const CANOPY_EL = 16;

/** Analytic position of a non-homing ordnance piece at a (fractional) tick. */
export function ordPosAt(kind: number, tick0: number, ox: number, oy: number, oz: number, vx: number, vy: number, vz: number, tick: number, out: { x: number; y: number; z: number }) {
  const t = Math.max(0, (tick - tick0) / TICK_RATE);
  const g = ORD[kind].gravity;
  out.x = ox + vx * t; out.y = oy + vy * t - 0.5 * g * t * t; out.z = oz + vz * t;
  return out;
}

/** The throw every client predicts and the server spawns: from the hand, along the aim, lofted. */
export function throwVelocity(yaw: number, pitch: number) {
  const d = aimDir(yaw, Math.min(1.2, pitch + 0.22), { x: 0, y: 0, z: 0 });
  return { vx: d.x * THROW_SPEED, vy: d.y * THROW_SPEED + 3, vz: d.z * THROW_SPEED };
}
export function handPos(p: PlayerState) {
  return { x: p.x - Math.sin(p.yaw) * 0.4, y: p.y + 1.6, z: p.z - Math.cos(p.yaw) * 0.4 };
}

interface Ord {
  /** the Duel Blaster's bolt: its charge (damage mul) and the element level it carries */
  mul?: number; level?: number;
  id: number; kind: OKind; sub: number; owner: number; tick0: number;
  ox: number; oy: number; oz: number; vx: number; vy: number; vz: number;
  x: number; y: number; z: number; px: number; py: number; pz: number;
  target: number; // enemy id for homing
  elem: number;
  /** fused grenades: tick it goes off (0 = impact), settled on the ground, robot it rides */
  fuseEnd: number; rest: boolean; stick: number; sx: number; sy: number; sz: number;
}
interface Zone { id: number; canopy: Canopy; owner: number; x: number; y: number; z: number; r: number; t: number }
interface Turret { id: number; owner: number; x: number; y: number; z: number; t: number; cd: number }
interface Pending { at: number; x: number; y: number; z: number; owner: number }
export interface Crate { id: number; wid: number; x: number; y: number; z: number; t: number }

/** What the Arsenal needs from the Game. */
export interface ArsenalHost {
  world: World;
  tick: number;
  seed: number;
  players: Map<number, PlayerState>;
  inputs: Map<number, InputFrame>;
  enemies: Enemy[];
  emit(t: Ev, f: number[], s?: string): void;
  damageEnemy(e: Enemy, amount: number, ap: number, by: number, flags: number, hx: number, hy: number, hz: number, bypassArmor?: boolean, bypassShield?: boolean): boolean;
  prime(e: Enemy, p: PlayerState): void;
  /** weight: a hit with this stagger force reels the robot if it beats its resistance (defs.ts STAGGER_*) */
  stagger(e: Enemy, force: number): void;
  openGas(x: number, y: number, z: number, r: number, dps: number, seconds: number, owner?: number): void;
  hurtPlayer(p: PlayerState, dmg: number, fx: number, fz: number, flags: number, kx?: number, kz?: number, bypassShield?: boolean): boolean;
  /** hurtPlayer from a cowboy's own ordnance or hazard; says who did it (Ev.FriendlyHit) */
  friendlyHurt(p: PlayerState, by: number, dmg: number, fx: number, fz: number, kx?: number, kz?: number, bypassShield?: boolean): boolean;
  /** a proc landed (bosses read elements in-fiction: the Angler's wet + shock) */
  procd?(e: Enemy, elem: number, owner: number): void;
  rng: Rng;
  /** an elemental blast landed at (x,y,z): a hazard it beats reacts, else it leaves its own (sim/elements.ts) */
  elemBlast?(elem: number, owner: number, x: number, y: number, z: number, scale?: number): void;
  onBlast?(x: number, y: number, z: number, r: number, dmg: number, by: number): void;
}

/** the belt holds GRENADE_MAX plus whatever the hand adds (Grenadier, Mule Pouch, Bandolier) */
export const nadeCap = (p: PlayerState) => GRENADE_MAX + modsOf(p).nadeAdd;
const CRATE_LIFE = secs(75);
/** grab reach: the same as a power-up crystal's (PICKUP_R), and a cowboy hovering low over one
 *  (feet up to CRATE_UP above it) or standing a step below it still takes it */
export const CRATE_R = 2.2;
export const CRATE_UP = 3.5;
export const CRATE_DOWN = 1.5;
const PULSE = 15;
/** a teammate standing in your hazard takes this fraction (CLAUDE.md gotcha 11) */
export const HAZARD_POSSE_MUL = 0.6;

export class Arsenal {
  private h: ArsenalHost;
  ords: Ord[] = [];
  zones: Zone[] = [];
  turrets: Turret[] = [];
  crates: Crate[] = [];
  private finales: Pending[] = [];
  private nextId = 1;
  private rng: Rng;
  private hit: RayHit = { t: 0, kind: 0, nx: 0, ny: 0, nz: 0 };
  private scratch = { x: 0, y: 0, z: 0 };

  constructor(h: ArsenalHost) {
    this.h = h;
    this.rng = new Rng(hash32(h.seed, 0xa75e));
  }
  private id() { const id = this.nextId++ & 0xffff; return id || (this.nextId++ & 0xffff); }
  reset() { this.ords = []; this.zones = []; this.turrets = []; this.crates = []; this.finales = []; }

  // ------------------------------------------------------------------ hooks from the Game
  /** a cowboy pulled the trigger on a non-hitscan weapon */
  fireOrdnance(p: PlayerState, inp: InputFrame, w: WeaponDef) {
    const d = aimDir(inp.yaw, inp.pitch, { x: 0, y: 0, z: 0 });
    const hp = handPos(p);
    switch (w.proj) {
      case Proj.Rocket: {
        const bad = w.id === W.BadIdea;
        const target = bad ? this.biggestEnemy(p, w.range) : -1;
        this.spawn(bad ? OKind.BadIdea : OKind.Rocket, w.id, p.id, hp.x, hp.y, hp.z, d.x * w.projSpeed, d.y * w.projSpeed, d.z * w.projSpeed, target, w.elem);
        break;
      }
      case Proj.Harpoon:
        this.spawn(OKind.Harpoon, w.id, p.id, hp.x, hp.y, hp.z, d.x * w.projSpeed, d.y * w.projSpeed + 1, d.z * w.projSpeed, -1, Elem.None);
        break;
      case Proj.Firework: case Proj.Beacon: {
        const v = throwVelocity(inp.yaw, inp.pitch);
        this.spawn(w.proj === Proj.Beacon ? OKind.Beacon : OKind.Shell, w.id, p.id, hp.x, hp.y, hp.z, v.vx, v.vy, v.vz, -1, w.elem);
        break;
      }
      case Proj.Turret: {
        let x = p.x - Math.sin(p.yaw) * 2, z = p.z - Math.cos(p.yaw) * 2;
        let y = this.h.world.support(x, z, p.y + 2);
        if (y < -1e6) { x = p.x; z = p.z; y = p.y; }
        for (const t of this.turrets) if (t.owner === p.id) t.t = Math.min(t.t, 1); // one sentry each
        const t: Turret = { id: this.id(), owner: p.id, x, y, z, t: secs(TURRET_SECS), cd: secs(0.6) };
        this.turrets.push(t);
        this.h.emit(Ev.Turret, [t.id, x, y, z, t.t, p.id]);
        break;
      }
    }
  }

  /** `cook` = ticks R1 was held: a cooked grenade has that much less fuse left when it lands */
  throwGrenade(p: PlayerState, inp: InputFrame, cook = 0) {
    const v = throwVelocity(inp.yaw, inp.pitch);
    const hp = handPos(p);
    const o = this.spawn(OKind.Grenade, p.nade, p.id, hp.x, hp.y, hp.z, v.vx, v.vy, v.vz, -1, GRENADES[p.nade]?.elem ?? Elem.None);
    const fuse = secs(GRENADES[p.nade]?.fuse ?? 0);
    if (fuse > 0) o.fuseEnd = this.h.tick + Math.max(1, fuse - cook);
  }

  /** held a fused grenade past its fuse: it goes off in the hand, and it hurts you too */
  cookOff(p: PlayerState) {
    const hp = handPos(p);
    const o = this.spawn(OKind.Grenade, p.nade, p.id, hp.x, hp.y, hp.z, 0, 0, 0, -1, GRENADES[p.nade]?.elem ?? Elem.None);
    this.ords.pop();
    this.h.emit(Ev.OrdEnd, [o.id, hp.x, hp.y, hp.z, 4]);
    this.grenadeBurst(o, null, true);
  }

  /** went down mid-cook: the live grenade drops at your feet with whatever fuse it had left */
  dropCooked(p: PlayerState) {
    if (p.cookT <= 0) return;
    const fuse = secs(GRENADES[p.nade]?.fuse ?? 0);
    const left = Math.max(1, fuse - p.cookT);
    p.cookT = 0; p.nades = Math.max(0, p.nades - 1);
    const o = this.spawn(OKind.Grenade, p.nade, p.id, p.x, p.y + 1, p.z, 0, 0, 0, -1, GRENADES[p.nade]?.elem ?? Elem.None);
    o.fuseEnd = this.h.tick + left;
  }

  private settle(o: Ord, e: Enemy | null) {
    o.rest = true; o.stick = 0;
    if (e && GRENADES[o.sub]?.sticky) {
      o.stick = e.id;
      o.sx = o.x - e.x; o.sy = o.y - e.y; o.sz = o.z - e.z;
    } else {
      const g = this.h.world.support(o.x, o.z, o.y + 0.5);
      if (g > -1e6 && o.y - g < 2.5) o.y = g + 0.1;
    }
    this.h.emit(Ev.OrdRest, [o.id, o.x, o.y, o.z, o.stick, o.fuseEnd]);
  }

  /** the shield soaked damage: it banks toward grenades and the special's next shot */
  onShieldDamage(p: PlayerState, amount: number) {
    if (amount <= 0) return;
    if (p.nades < nadeCap(p)) p.nadeBank += amount;
    if (p.w2 !== NO_WEAPON) p.bank += amount;
  }

  /** Bolt-Action Sniper: racking the first round into an empty gun shoves the porch clear */
  shove(p: PlayerState, force: number) {
    for (const e of this.h.enemies) {
      if (e.dead) continue;
      const dx = e.x - p.x, dz = e.z - p.z, d = len2(dx, dz);
      if (d > 5 || Math.abs(e.y - p.y) > 2.5) continue;
      const k = force * (1 - d / 6) / ENEMIES[e.type].weight;
      e.kx += (dx / (d || 1)) * k; e.kz += (dz / (d || 1)) * k;
    }
    this.h.emit(Ev.Explosion, [p.x, p.y + 0.9, p.z, 5, 4]);
  }

  onEnemyKilled(e: Enemy) {
    // heavies carry ordnance home (GAMEPLAY_LOOP §2.5: specials are gifts from the run)
    const r = this.rng.next();
    const chance = e.type === EType.MetalBrute ? 1 : e.type === EType.Brute ? 0.4 : e.type === EType.Bomber || e.type === EType.Engineer ? 0.1 : 0.015;
    if (r < chance) this.dropCrate(e.x, e.z, e.y);
  }

  /** a wave ended: one grenade back for everyone; a crate after the second wave and every clear */
  onWaveEnd(wave: number, cleared: boolean) {
    for (const p of this.h.players.values()) if (p.nades < nadeCap(p) && p.phase === Phase.Alive) p.nades++;
    if (wave === 2 || cleared) {
      const c = this.posseCenter();
      this.dropCrate(c.x + this.rng.range(-4, 4), c.z + this.rng.range(-4, 4), c.y + 4);
    }
  }

  /** re-announce live world state to someone who just joined */
  announce() {
    for (const c of this.crates) this.h.emit(Ev.Crate, [c.id, c.wid, c.x, c.y, c.z, 1, 255]);
  }

  // ------------------------------------------------------------------ per tick
  step() {
    const h = this.h;
    for (const p of h.players.values()) this.bankTick(p);
    this.stepOrds();
    this.stepZones();
    this.stepTurrets();
    for (let i = this.finales.length - 1; i >= 0; i--) {
      const f = this.finales[i];
      if (h.tick < f.at) continue;
      this.finales.splice(i, 1);
      const w = WEAPONS[W.BigOne];
      this.blast(f.x, f.y, f.z, w.blast, w.damage, w.ap, f.owner, Elem.None, 0);
    }
    this.stepCrates();
  }

  private bankTick(p: PlayerState) {
    if (p.nades < nadeCap(p) && p.nadeBank >= GRENADE_BANK) { p.nades++; p.nadeBank -= GRENADE_BANK; }
    if (p.nades >= nadeCap(p)) p.nadeBank = 0;
    if (p.w2 === NO_WEAPON) { p.bank = 0; return; }
    const w = weaponDef(p.w2);
    const cur = p.wSlot === WSlot.Special ? p.mag : p.mag2;
    if (cur < w.mag && p.bank >= w.recharge) {
      p.bank -= w.recharge;
      if (p.wSlot === WSlot.Special) p.mag++; else p.mag2++;
    }
    // a full special keeps its progress (Godot _special_recharge_progress is never thrown away), up to one more mag
    if (cur >= w.mag) p.bank = Math.min(p.bank, w.recharge * w.mag);
  }

  private spawn(kind: OKind, sub: number, owner: number, x: number, y: number, z: number, vx: number, vy: number, vz: number, target: number, elem: number) {
    const o: Ord = { id: this.id(), kind, sub, owner, tick0: this.h.tick, ox: x, oy: y, oz: z, vx, vy, vz, x, y, z, px: x, py: y, pz: z, target, elem,
      fuseEnd: 0, rest: false, stick: 0, sx: 0, sy: 0, sz: 0 };
    this.ords.push(o);
    this.h.emit(Ev.OrdSpawn, [o.id, kind, sub, o.tick0, x, y, z, vx, vy, vz, owner, target < 0 ? 0 : target]);
    return o;
  }

  private stepOrds() {
    const h = this.h;
    for (let i = this.ords.length - 1; i >= 0; i--) {
      const o = this.ords[i];
      const def = ORD[o.kind];
      o.px = o.x; o.py = o.y; o.pz = o.z;
      if (o.rest) {
        // a settled (or stuck) fused grenade waits out its fuse
        if (o.stick) {
          const e = h.enemies.find((q) => q.id === o.stick && !q.dead);
          if (e) { o.x = e.x + o.sx; o.y = e.y + o.sy; o.z = e.z + o.sz; }
          else o.stick = 0; // its robot died: it stays where it fell
        }
        if (h.tick < o.fuseEnd) continue;
        this.ords.splice(i, 1);
        h.emit(Ev.OrdEnd, [o.id, o.x, o.y, o.z, 3]);
        this.detonate(o, o.stick ? h.enemies.find((q) => q.id === o.stick) ?? null : null, 3);
        continue;
      }
      if (def.homing > 0) {
        // steer toward the target (re-pick the nearest if it died)
        let t = o.target > 0 ? h.enemies.find((e) => e.id === o.target && !e.dead) : undefined;
        if (!t && o.kind === OKind.Seeker) { t = this.nearestEnemy(o.x, o.y, o.z, 25, -1); if (t) o.target = t.id; }
        const speed = o.kind === OKind.Seeker ? SEEKER_SPEED : o.kind === OKind.Bolt ? DUEL_BOLT_SPEED : weaponDef(o.sub).projSpeed;
        if (t) {
          const ty = t.y + ENEMIES[t.type].bodyY;
          let dx = t.x - o.x, dy = ty - o.y, dz = t.z - o.z;
          const dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
          dx /= dl; dy /= dl; dz /= dl;
          const k = Math.min(1, def.homing * DT);
          let nx = o.vx / speed + (dx - o.vx / speed) * k, ny = o.vy / speed + (dy - o.vy / speed) * k, nz = o.vz / speed + (dz - o.vz / speed) * k;
          const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
          nx /= nl; ny /= nl; nz /= nl;
          o.vx = nx * speed; o.vy = ny * speed; o.vz = nz * speed;
        }
        o.x += o.vx * DT; o.y += o.vy * DT; o.z += o.vz * DT;
      } else {
        ordPosAt(o.kind, o.tick0, o.ox, o.oy, o.oz, o.vx, o.vy, o.vz, h.tick, this.scratch);
        o.x = this.scratch.x; o.y = this.scratch.y; o.z = this.scratch.z;
      }
      let end = 0; // 1 world 2 enemy 3 expired
      let hitE: Enemy | null = null;
      const dx = o.x - o.px, dy = o.y - o.py, dz = o.z - o.pz;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d > 1e-5 && o.kind !== OKind.Launch) {
        h.world.raycast(o.px, o.py, o.pz, dx / d, dy / d, dz / d, d, this.hit);
        if (this.hit.kind !== 0) {
          const t = this.hit.t;
          o.x = o.px + (dx / d) * t; o.y = o.py + (dy / d) * t; o.z = o.pz + (dz / d) * t;
          end = 1;
        }
      }
      if (o.kind !== OKind.Shell && o.kind !== OKind.Beacon && o.kind !== OKind.Launch) {
        for (const e of h.enemies) {
          if (e.dead) continue;
          const ed = ENEMIES[e.type];
          const rr = ed.radius + def.radius + (o.kind === OKind.Seeker ? 0.4 : 0);
          if (segPointDist2(o.px, o.py, o.pz, o.x, o.y, o.z, e.x, e.y + ed.bodyY, e.z) <= rr * rr) { hitE = e; end = 2; break; }
        }
      }
      if (o.fuseEnd > 0) {
        // fused: a hit settles it (or sticks it), the fuse decides when it goes, even mid-air
        if (h.tick >= o.fuseEnd || o.y < -80) end = 3;
        else if (end) { this.settle(o, hitE); continue; }
        else continue;
      }
      if (!end && (h.tick - o.tick0) * DT > def.life) end = 3;
      if (!end && o.y < -80) end = 3;
      if (!end) continue;
      this.ords.splice(i, 1);
      h.emit(Ev.OrdEnd, [o.id, o.x, o.y, o.z, end]);
      this.detonate(o, hitE, end);
    }
  }

  private detonate(o: Ord, hitE: Enemy | null, end: number) {
    const h = this.h;
    switch (o.kind) {
      case OKind.Grenade: this.grenadeBurst(o, hitE); break;
      case OKind.Rocket: case OKind.BadIdea: {
        const w = weaponDef(o.sub);
        if (hitE) h.damageEnemy(hitE, w.damage * 0.5, w.ap, o.owner, 0, o.x, o.y, o.z);
        this.blast(o.x, o.y, o.z, w.blast, w.damage, w.ap, o.owner, o.elem, 0);
        if (o.kind === OKind.BadIdea) {
          // Bad Idea: it does not care who fired it
          const p = h.players.get(o.owner);
          if (p && len2(p.x - o.x, p.z - o.z) < w.blast && Math.abs(p.y - o.y) < w.blast) {
            const kl = len2(p.x - o.x, p.z - o.z) || 1;
            h.friendlyHurt(p, o.owner, 55, o.x, o.z, ((p.x - o.x) / kl) * 14, ((p.z - o.z) / kl) * 14);
          }
        }
        break;
      }
      case OKind.Harpoon: {
        const w = weaponDef(o.sub);
        if (hitE) {
          h.damageEnemy(hitE, w.damage, w.ap, o.owner, 0, o.x, o.y, o.z);
          if (!hitE.dead) {
            hitE.stunT = Math.max(hitE.stunT, hitE.maxHp >= 300 ? secs(0.6) : secs(1.5));
            const wt = ENEMIES[hitE.type].weight;
            const vl = Math.sqrt(o.vx * o.vx + o.vz * o.vz) || 1;
            hitE.kx += (o.vx / vl) * w.push / wt; hitE.kz += (o.vz / vl) * w.push / wt;
          }
        }
        h.openGas(o.x, o.y, o.z, w.blast, 10, 4, o.owner);
        break;
      }
      case OKind.Shell: {
        // landed: the shell kicks a rocket straight up, which bursts at apex
        const y = end === 1 ? o.y + 0.2 : o.y;
        this.spawn(OKind.Launch, o.sub, o.owner, o.x, y, o.z, 0, LAUNCH_SPEED, 0, -1, o.elem);
        break;
      }
      case OKind.Launch: this.burst(o); break;
      case OKind.Beacon: {
        this.finales.push({ at: h.tick + secs(BEACON_SECS), x: o.x, y: o.y, z: o.z, owner: o.owner });
        break;
      }
      case OKind.Bolt: {
        // the Duel Blaster's plasma lands: shields take it at shieldMul, the rest at the normal rate (the hitscan rule)
        const w = weaponDef(o.sub);
        if (hitE) {
          let dmg = w.damage * (o.mul ?? (o.target >= 0 ? PLASMA_FULL_MUL : 1));
          if (w.shieldMul !== 1 && hitE.shield > 0) { const vs = dmg * w.shieldMul; dmg = vs <= hitE.shield ? vs : hitE.shield + (vs - hitE.shield) / w.shieldMul; }
          const killed = h.damageEnemy(hitE, dmg, w.ap, o.owner, 0, o.x, o.y, o.z);
          if (!killed && o.elem !== Elem.None && (o.level ?? 0) > 0) tryProc(this.h as unknown as ElemHost, hitE, o.elem, o.level ?? 1, o.owner, dmg); // the pistol's install
          if (!killed) hitE.flinchT = 2;
        }
        break;
      }
      case OKind.Seeker: {
        const w = weaponDef(o.sub);
        if (hitE) {
          const killed = h.damageEnemy(hitE, w.damage, w.ap, o.owner, 0, o.x, o.y, o.z);
          if (!killed) {
            hitE.stunT = Math.max(hitE.stunT, hitE.maxHp >= 300 ? secs(0.3) : secs(1));
            this.proc(hitE, o.elem, o.owner);
          }
        }
        break;
      }
    }
  }

  /** a firework shell bursts at apex: seekers, a canopy, or the finale */
  private burst(o: Ord) {
    const h = this.h, w = weaponDef(o.sub);
    h.emit(Ev.Explosion, [o.x, o.y, o.z, 2.5, 5]);
    if (w.payload === Payload.Seekers) {
      const n = w.elem === Elem.Rainbow ? 15 : 6;
      const near = h.enemies.filter((e) => !e.dead && len2(e.x - o.x, e.z - o.z) < w.blast)
        .sort((a, b) => len2(a.x - o.x, a.z - o.z) - len2(b.x - o.x, b.z - o.z));
      const RAINBOW = [Elem.Fire, Elem.Ice, Elem.Shock, Elem.Acid, Elem.Magnetic];
      for (let i = 0; i < n; i++) {
        const t = near.length ? near[i % near.length] : null;
        const a = (i / n) * Math.PI * 2;
        const el = w.elem === Elem.Rainbow ? RAINBOW[i % 5] : w.elem;
        this.spawn(OKind.Seeker, w.id, o.owner, o.x, o.y, o.z, Math.cos(a) * SEEKER_SPEED * 0.6, -SEEKER_SPEED * 0.3, Math.sin(a) * SEEKER_SPEED * 0.6, t ? t.id : -1, el);
      }
    } else if (w.payload === Payload.Canopy) {
      const gy = h.world.support(o.x, o.z, o.y);
      const z: Zone = { id: this.id(), canopy: w.canopy, owner: o.owner, x: o.x, y: gy > -1e6 ? gy : o.y - LAUNCH_SPEED, z: o.z, r: w.blast, t: secs(CANOPY_SECS) };
      this.zones.push(z);
      h.emit(Ev.Hazard, [z.id, CANOPY_EL + z.canopy, z.x, z.y, z.z, z.r, z.t]);
    }
  }

  private grenadeBurst(o: Ord, direct: Enemy | null, selfToo = false) {
    const h = this.h, g = GRENADES[o.sub] ?? GRENADES[G.Impact];
    if (g.gasSecs > 0) { h.openGas(o.x, o.y, o.z, g.radius, g.gasDps, g.gasSecs, selfToo ? -1 : o.owner); return; }
    const owner = h.players.get(o.owner);
    const primes = owner?.cls === ClassId.Juggernaut; // the Juggernaut's secondary primer (GAMEPLAY_LOOP §1.2)
    h.emit(Ev.Explosion, [o.x, o.y, o.z, g.radius, g.stun > 0 ? 4 : g.slow > 0 ? 6 : 0]);
    blastWorld(h, o.x, o.y, o.z, g.radius, g.damage, o.owner);
    for (const e of [...h.enemies]) {
      if (e.dead) continue;
      const ed = ENEMIES[e.type];
      const d = Math.sqrt((e.x - o.x) ** 2 + (e.y + ed.bodyY - o.y) ** 2 + (e.z - o.z) ** 2) - ed.radius * 0.5;
      if (d > g.radius && e !== direct) continue;
      const f = e === direct ? 1 : 1 - clamp(d / g.radius, 0, 1) * 0.5;
      let killed = false;
      if (g.damage > 0) killed = h.damageEnemy(e, g.damage * f, e === direct ? g.ap : Math.max(0, g.ap - SPLASH_AP_DROP), o.owner, 0, e.x, e.y + ed.bodyY, e.z, false, g.elem === Elem.Magnetic);
      if (killed) continue;
      const heavy = e.maxHp >= 300;
      if (g.stun > 0) e.stunT = Math.max(e.stunT, secs(heavy ? g.stun / 3 : g.stun));
      if (g.slow > 0) e.slowT = Math.max(e.slowT, secs(g.slow));
      this.proc(e, g.elem, o.owner, Math.max(1, h.players.get(o.owner)?.nadeLevel ?? 1)); // the cache's level
      if (g.damage > 0) {
        const kx = e.x - o.x, kz = e.z - o.z, kl = len2(kx, kz) || 1;
        e.kx += (kx / kl) * 6 * f / ed.weight; e.kz += (kz / kl) * 6 * f / ed.weight;
        h.stagger(e, STAGGER_GRENADE * f);
      }
      if (primes && owner) h.prime(e, owner);
    }
    if (g.damage > 0) this.hurtPosse(o.x, o.y, o.z, g.radius, g.damage, selfToo ? -1 : o.owner, 6, g.elem === Elem.Magnetic, o.owner);
    if (g.elem !== Elem.None && g.elem !== Elem.Rainbow) h.elemBlast?.(g.elem, o.owner, o.x, o.y, o.z); // the grenade's hazard, or a reaction
    if (g.slow > 0) {
      // the Time grenade doesn't care whose side you're on (James, 2026-10-21); it skips the thrower like every blast
      for (const p of h.players.values()) {
        if ((p.id === o.owner && !selfToo) || p.phase !== Phase.Alive) continue;
        const d = Math.sqrt((p.x - o.x) ** 2 + (p.y + PLAYER_HEIGHT * 0.5 - o.y) ** 2 + (p.z - o.z) ** 2) - PLAYER_RADIUS;
        if (d <= g.radius) p.timeT = Math.max(p.timeT, secs(g.slow));
      }
    }
  }

  /** Friendly fire is always on (James, 2026-10-21; Godot parity): a blast catches every cowboy in
   *  range except the one who set it off. Full damage, same falloff as robots. */
  hurtPosse(x: number, y: number, z: number, r: number, dmg: number, owner: number, knock: number, bypassShield = false, by = owner) {
    const h = this.h;
    for (const p of h.players.values()) {
      if (p.id === owner) continue;
      const d = Math.sqrt((p.x - x) ** 2 + (p.y + PLAYER_HEIGHT * 0.5 - y) ** 2 + (p.z - z) ** 2) - PLAYER_RADIUS;
      if (d > r) continue;
      const f = 1 - clamp(d / r, 0, 1) * 0.5;
      const kx = p.x - x, kz = p.z - z, kl = len2(kx, kz) || 1;
      h.friendlyHurt(p, by, dmg * f, x, z, (kx / kl) * knock * f, (kz / kl) * knock * f, bypassShield);
    }
  }

  /** the Duel Blaster (playtest 1, Nick: "slow tracking shots"): a plasma bolt from the hand, homing on `target` (-1 flies straight) */
  spawnBolt(p: PlayerState, w: WeaponDef, dx: number, dy: number, dz: number, target: number, mul = 1, ox = 0, oz = 0, elem = Elem.None, level = 0) {
    const hp = handPos(p);
    const o = this.spawn(OKind.Bolt, w.id, p.id, hp.x + ox, hp.y, hp.z + oz, dx * DUEL_BOLT_SPEED, dy * DUEL_BOLT_SPEED, dz * DUEL_BOLT_SPEED, target, elem);
    o.mul = mul; o.level = level;
    return o;
  }

  /** an ordnance-grade blast at a point (tests, future hazards): robots and, friendly fire, every cowboy but `owner` */
  blastAt(x: number, y: number, z: number, r: number, dmg: number, ap: number, owner: number, elem = Elem.None, level = 1) { this.blast(x, y, z, r, dmg, ap, owner, elem, 0, level); }

  /** ordnance blast: robots, and (friendly fire) every cowboy but the shooter */
  private blast(x: number, y: number, z: number, r: number, dmg: number, ap: number, owner: number, elem: number, kind: number, level = 1) {
    const h = this.h;
    h.emit(Ev.Explosion, [x, y, z, r, kind]);
    blastWorld(h, x, y, z, r, dmg, owner);
    h.onBlast?.(x, y, z, r, dmg, owner); // panel shed, barrels (sim/game.ts)
    for (const e of [...h.enemies]) {
      if (e.dead) continue;
      const ed = ENEMIES[e.type];
      const d = Math.sqrt((e.x - x) ** 2 + (e.y + ed.bodyY - y) ** 2 + (e.z - z) ** 2) - ed.radius * 0.5;
      if (d > r) continue;
      const f = 1 - clamp(d / r, 0, 1) * 0.5;
      const killed = h.damageEnemy(e, dmg * f, Math.max(0, ap - SPLASH_AP_DROP), owner, 0, e.x, e.y + ed.bodyY, e.z, false, elem === Elem.Magnetic); // splash lands a tier under the warhead
      if (killed) continue;
      if (elem === Elem.Rainbow) for (const el of [Elem.Fire, Elem.Ice, Elem.Shock, Elem.Acid, Elem.Magnetic]) this.proc(e, el, owner);
      else this.proc(e, elem, owner, level);
      const kx = e.x - x, kz = e.z - z, kl = len2(kx, kz) || 1;
      e.kx += (kx / kl) * 12 * f / ed.weight; e.kz += (kz / kl) * 12 * f / ed.weight;
      e.stunT = Math.max(e.stunT, e.maxHp >= 300 ? 0 : secs(0.35));
      h.stagger(e, STAGGER_ORDNANCE * f);
    }
    this.hurtPosse(x, y, z, r, dmg, owner, 12, elem === Elem.Magnetic);
  }

  /** an elemental proc on a robot (GAMEPLAY_LOOP §1.6 element table, simplified for the port) */
  proc(e: Enemy, elem: number, owner: number, level = 1, amount = 10) {
    if (e.dead || elem === Elem.None || elem === Elem.Rainbow) return;
    applyProc(this.h as unknown as ElemHost, e, elem, level, owner, amount); // the element rules (sim/elements.ts), forced: ordnance always procs
  }
  /** the old simplified table (kept for reference; the rules above replaced it 2026-10-22) */
  private procLegacy(e: Enemy, elem: number, owner: number) {
    if (e.dead || elem === Elem.None || elem === Elem.Rainbow) return;
    const h = this.h, heavy = e.maxHp >= 300;
    switch (elem) {
      case Elem.Fire: e.burnT = Math.max(e.burnT, secs(4)); e.burnOwner = owner; break;
      case Elem.Ice:
        if (heavy) e.slowT = Math.max(e.slowT, secs(4));
        else { e.stunT = Math.max(e.stunT, secs(1.4)); e.slowT = Math.max(e.slowT, secs(3)); }
        break;
      case Elem.Shock: {
        e.stunT = Math.max(e.stunT, secs(heavy ? 0.2 : 0.6));
        // arc to the two nearest
        let n = 0;
        for (const o of h.enemies) {
          if (o === e || o.dead || n >= 2) continue;
          if (len2(o.x - e.x, o.z - e.z) > 6) continue;
          n++;
          h.damageEnemy(o, 20, AP.Medium, owner, 0, o.x, o.y + 1, o.z);
          if (!o.dead) o.stunT = Math.max(o.stunT, secs(0.3));
        }
        break;
      }
      case Elem.Acid: e.stripT = Math.max(e.stripT, secs(6)); e.vulnT = Math.max(e.vulnT, secs(5)); break;
      case Elem.Magnetic:
        if (e.shield > 0) h.damageEnemy(e, e.shield, AP.Ordnance, owner, 2, e.x, e.y + 1, e.z, true);
        e.shieldRegenT = Math.max(e.shieldRegenT, secs(10));
        break;
    }
    h.emit(Ev.Proc, [e.id, elem]);
    h.procd?.(e, elem, owner);
  }

  // ------------------------------------------------------------------ canopies
  private stepZones() {
    const h = this.h;
    for (let i = this.zones.length - 1; i >= 0; i--) {
      const z = this.zones[i];
      if (--z.t <= 0) { this.zones.splice(i, 1); continue; }
      if (z.t % PULSE !== 0) continue;
      const inside = (x: number, y: number, zz: number) => len2(x - z.x, zz - z.z) < z.r && y > z.y - 3 && y < z.y + 9;
      switch (z.canopy) {
        case Canopy.Coolant: case Canopy.Mending:
          for (const p of h.players.values()) {
            if (p.phase !== Phase.Alive || !inside(p.x, p.y, p.z)) continue;
            if (z.canopy === Canopy.Coolant) p.heat = Math.max(0, p.heat - 18 * PULSE * DT);
            else p.hp = Math.min(100, p.hp + 3 * PULSE * DT);
          }
          break;
        case Canopy.Aegis: break; // blocks bullets: see blocksBullet
        default: {
          const targets = h.enemies.filter((e) => !e.dead && inside(e.x, e.y, e.z));
          if (z.canopy === Canopy.Shock) {
            // lightning picks one robot per pulse
            if (targets.length) {
              const e = targets[this.rng.int(0, targets.length - 1)];
              if (!h.damageEnemy(e, 18, AP.Medium, z.owner, 0, e.x, e.y + 1, e.z)) this.proc(e, Elem.Shock, z.owner);
            }
            break;
          }
          if (z.canopy === Canopy.Fire) {
            // standing hazards hit the posse too at ~0.6x, never the one who placed them (CLAUDE.md gotcha 11)
            for (const p of h.players.values()) {
              if (p.id !== z.owner && p.phase === Phase.Alive && inside(p.x, p.y, p.z)) h.friendlyHurt(p, z.owner, 6 * PULSE * DT * 4 * HAZARD_POSSE_MUL, z.x, z.z, 0, 0, true);
            }
          }
          for (const e of targets) {
            if (z.canopy === Canopy.Fire) { if (!h.damageEnemy(e, 6 * PULSE * DT * 4, AP.Light, z.owner, 0, e.x, e.y + 1, e.z, true)) { e.burnT = Math.max(e.burnT, secs(1.5)); e.burnOwner = z.owner; } }
            else if (z.canopy === Canopy.Ice) e.slowT = Math.max(e.slowT, secs(1));
            else if (z.canopy === Canopy.Acid) { e.stripT = Math.max(e.stripT, secs(1)); e.vulnT = Math.max(e.vulnT, secs(1)); }
            else if (z.canopy === Canopy.Magnetic) {
              if (e.shield > 0) h.damageEnemy(e, Math.min(e.shield, 25), AP.Ordnance, z.owner, 2, e.x, e.y + 1, e.z, true);
              e.shieldRegenT = Math.max(e.shieldRegenT, secs(2));
            }
          }
        }
      }
    }
  }

  /** Aegis Canopy: a dome that eats enemy fire crossing into it */
  blocksBullet(x: number, y: number, z: number) {
    for (const zn of this.zones) {
      if (zn.canopy !== Canopy.Aegis) continue;
      const dx = x - zn.x, dy = y - zn.y, dz = z - zn.z;
      if (dx * dx + dy * dy + dz * dz < zn.r * zn.r) return true;
    }
    return false;
  }

  // ------------------------------------------------------------------ turrets
  private stepTurrets() {
    const h = this.h;
    for (let i = this.turrets.length - 1; i >= 0; i--) {
      const t = this.turrets[i];
      if (--t.t <= 0) { this.turrets.splice(i, 1); h.emit(Ev.TurretShot, [t.id, 0, 0, 0, 0, 255]); continue; }
      if (--t.cd > 0) continue;
      t.cd = secs(0.22);
      const ty = t.y + 1.1;
      let best: Enemy | null = null, bd = WEAPONS[W.Turret].range;
      for (const e of h.enemies) {
        if (e.dead) continue;
        const d = Math.sqrt((e.x - t.x) ** 2 + (e.z - t.z) ** 2);
        if (d >= bd) continue;
        if (!h.world.lineOfSight(t.x, ty, t.z, e.x, e.y + ENEMIES[e.type].bodyY, e.z)) continue;
        best = e; bd = d;
      }
      if (!best) { t.cd = secs(0.4); continue; }
      const ex = best.x, ey = best.y + ENEMIES[best.type].bodyY, ez = best.z;
      const dx = ex - t.x, dy = ey - ty, dz = ez - t.z, dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
      h.damageEnemy(best, WEAPONS[W.Turret].damage, AP.Medium, t.owner, 0, ex, ey, ez);
      h.emit(Ev.TurretShot, [t.id, dx / dl, dy / dl, dz / dl, dl, 1]);
    }
  }

  // ------------------------------------------------------------------ crates
  dropCrate(x: number, z: number, yHint: number) {
    const h = this.h;
    let y = h.world.support(x, z, yHint + 3);
    if (y < -1e6) {
      // over the void: bring it in toward the posse
      const c = this.posseCenter();
      x = c.x; z = c.z; y = h.world.support(x, z, c.y + 3);
      if (y < -1e6) return;
    }
    const wid = rollCrate(this.rng.next());
    const c: Crate = { id: this.id(), wid, x, y, z, t: CRATE_LIFE };
    this.crates.push(c);
    h.emit(Ev.Crate, [c.id, wid, x, y, z, 1, 255]);
    h.emit(Ev.Notice, [], `ORDNANCE CRATE: ${WEAPONS[wid].name.toUpperCase()}`);
  }

  private stepCrates() {
    const h = this.h;
    for (let i = this.crates.length - 1; i >= 0; i--) {
      const c = this.crates[i];
      if (--c.t <= 0) { this.crates.splice(i, 1); h.emit(Ev.Crate, [c.id, c.wid, c.x, c.y, c.z, 0, 255]); continue; }
      for (const p of h.players.values()) {
        if (p.phase !== Phase.Alive || !isTargetable(p)) continue;
        if (len2(p.x - c.x, p.z - c.z) > CRATE_R || p.y - c.y > CRATE_UP || c.y - p.y > CRATE_DOWN) continue;
        const empty = p.w2 === NO_WEAPON || (p.wSlot === WSlot.Special ? p.mag : p.mag2) === 0;
        const asks = ((h.inputs.get(p.id)?.buttons ?? 0) & 64) !== 0; // Interact (□) swaps a loaded special
        if (!empty && !asks) continue;
        const w = weaponDef(c.wid);
        p.w2 = c.wid; p.bank = 0;
        if (p.wSlot === WSlot.Special) p.mag = w.mag; else p.mag2 = w.mag;
        this.crates.splice(i, 1);
        h.emit(Ev.Crate, [c.id, c.wid, c.x, c.y, c.z, 0, p.id]);
        break;
      }
    }
  }

  // ------------------------------------------------------------------ helpers
  private nearestEnemy(x: number, y: number, z: number, r: number, skip: number) {
    let best: Enemy | undefined, bd = r;
    for (const e of this.h.enemies) {
      if (e.dead || e.id === skip) continue;
      const d = len2(e.x - x, e.z - z) + Math.abs(e.y - y) * 0.5;
      if (d < bd) { bd = d; best = e; }
    }
    return best;
  }
  private biggestEnemy(p: PlayerState, range: number) {
    let best = -1, bs = 0;
    const fx = -Math.sin(p.yaw), fz = -Math.cos(p.yaw);
    for (const e of this.h.enemies) {
      if (e.dead) continue;
      const dx = e.x - p.x, dz = e.z - p.z, d = len2(dx, dz);
      if (d > range || d < 1) continue;
      const facing = (dx * fx + dz * fz) / d;
      if (facing < 0.2) continue;
      const score = e.maxHp * (0.5 + facing);
      if (score > bs) { bs = score; best = e.id; }
    }
    return best;
  }
  private posseCenter() {
    let x = 0, y = 0, z = 0, k = 0;
    for (const p of this.h.players.values()) if (isTargetable(p) && p.phase === Phase.Alive) { x += p.lastGx; y += p.lastGy; z += p.lastGz; k++; }
    return k ? { x: x / k, y: y / k, z: z / k } : { x: 0, y: 0, z: 0 };
  }
}

export { activeWeaponId, weaponOf };
